import React, { useState } from "react";
import {
  Button,
  Drawer,
  Checkbox,
  Space,
  Tag,
  Divider,
  Typography,
  Row,
  Col,
  Badge,
  Card,
  Collapse,
} from "antd";
import { FilterOutlined, ClearOutlined } from "@ant-design/icons";

import { subfoldersMapping } from "../fixtures/subfoldersMapping";
import { requirementsMapping } from "../fixtures/requirementsMapping";

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface HistoryFiltersProps {
  availableRoles: string[];
  availableResourceTypes: string[];
  availableSubfolderSlugs: string[];
  availableRequirementSlugs: string[];
  filters: {
    roles: string[];
    resourceTypes: string[];
    subfolderSlugs: string[];
    requirementSlugs: string[];
  };
  onFiltersChange: (filters: any) => void;
  totalCount: number;
  filteredCount: number;
}

const getSubfolderLabel = (slug: string): string => {
  return subfoldersMapping[slug] || slug;
};

const getRequirementLabel = (slug: string): string => {
  return requirementsMapping[slug] || slug;
};

export const HistoryFilters: React.FC<HistoryFiltersProps> = ({
  availableRoles,
  availableResourceTypes,
  availableSubfolderSlugs,
  availableRequirementSlugs,
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
}) => {
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleRoleChange = (checkedValues: string[]) => {
    onFiltersChange({ ...filters, roles: checkedValues });
  };

  const handleResourceTypeChange = (checkedValues: string[]) => {
    onFiltersChange({ ...filters, resourceTypes: checkedValues });
  };

  const handleSubfolderSlugChange = (checkedValues: string[]) => {
    onFiltersChange({ ...filters, subfolderSlugs: checkedValues });
  };

  const handleRequirementSlugChange = (checkedValues: string[]) => {
    onFiltersChange({ ...filters, requirementSlugs: checkedValues });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      roles: [],
      resourceTypes: [],
      subfolderSlugs: [],
      requirementSlugs: [],
    });
  };

  const removeFilter = (
    type: "roles" | "resourceTypes" | "subfolderSlugs" | "requirementSlugs",
    value: string,
  ) => {
    const newFilters = { ...filters };
    newFilters[type] = newFilters[type].filter((item) => item !== value);
    onFiltersChange(newFilters);
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      tenant: "purple",
      guarantor: "purple",
      landlord: "purple",
      operations: "green",
      cja: "green",
      advisor: "purple",
      system: "gray",
      admin: "red",
    };
    return colors[role] || "default";
  };

  const activeFiltersCount =
    filters.roles.length +
    filters.resourceTypes.length +
    filters.subfolderSlugs.length +
    filters.requirementSlugs.length;

  return (
    <>
      {/* Filter Summary Bar */}
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "#fafafa",
          borderRadius: 6,
          border: "1px solid #d9d9d9",
          marginBottom: 16,
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Mostrando <Text strong>{filteredCount}</Text> de{" "}
                <Text strong>{totalCount}</Text> registros
              </Text>

              {/* Quick filter preview */}
              {activeFiltersCount > 0 && (
                <>
                  <Divider type="vertical" />
                  <Space size={[4, 4]} wrap>
                    {filters.roles.slice(0, 2).map((role) => (
                      <Tag
                        key={role}
                        color={getRoleColor(role)}
                        closable
                        onClose={() => removeFilter("roles", role)}
                        style={{ fontSize: 11, margin: 0 }}
                      >
                        {role}
                      </Tag>
                    ))}
                    {filters.resourceTypes.slice(0, 1).map((type) => (
                      <Tag
                        key={type}
                        closable
                        onClose={() => removeFilter("resourceTypes", type)}
                        style={{ fontSize: 11, margin: 0 }}
                      >
                        {type}
                      </Tag>
                    ))}
                    {activeFiltersCount > 3 && (
                      <Tag style={{ fontSize: 11, margin: 0 }}>
                        +{activeFiltersCount - 3} más
                      </Tag>
                    )}
                  </Space>
                </>
              )}
            </Space>
          </Col>

          <Col>
            <Space>
              {activeFiltersCount > 0 && (
                <Button
                  type="text"
                  size="small"
                  onClick={clearAllFilters}
                  style={{ fontSize: 12 }}
                >
                  Limpiar filtros
                </Button>
              )}

              <Badge count={activeFiltersCount} size="small">
                <Button
                  type="default"
                  size="small"
                  icon={<FilterOutlined />}
                  onClick={() => setDrawerVisible(true)}
                >
                  Filtros
                </Button>
              </Badge>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Drawer */}
      <Drawer
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Title level={4} style={{ margin: 0 }}>
              Filtrar Historial
            </Title>
            {activeFiltersCount > 0 && (
              <Button
                type="link"
                onClick={clearAllFilters}
                icon={<ClearOutlined />}
                style={{ padding: 15 }}
              >
                Quitar filtros
              </Button>
            )}
          </div>
        }
        placement="right"
        width={420}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        extra={
          <Button type="primary" onClick={() => setDrawerVisible(false)}>
            Ok
          </Button>
        }
      >
        <div style={{ paddingBottom: 20 }}>
          {/* Active Filters Summary */}
          {activeFiltersCount > 0 && (
            <Card
              size="small"
              style={{ marginBottom: 16, backgroundColor: "#f6ffed" }}
            >
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                FILTROS ACTIVOS ({activeFiltersCount})
              </Text>
              <div style={{ marginTop: 8 }}>
                <Space size={[4, 4]} wrap>
                  {filters.roles.map((role) => (
                    <Tag
                      key={role}
                      color={getRoleColor(role)}
                      closable
                      onClose={() => removeFilter("roles", role)}
                    >
                      {role}
                    </Tag>
                  ))}
                  {filters.resourceTypes.map((type) => (
                    <Tag
                      key={type}
                      closable
                      onClose={() => removeFilter("resourceTypes", type)}
                    >
                      {type}
                    </Tag>
                  ))}
                  {filters.subfolderSlugs.map((slug) => (
                    <Tag
                      key={`subfolder-${slug}`}
                      color="cyan"
                      closable
                      onClose={() => removeFilter("subfolderSlugs", slug)}
                    >
                      📁 {getSubfolderLabel(slug)}
                    </Tag>
                  ))}
                  {filters.requirementSlugs.map((slug) => (
                    <Tag
                      key={`requirement-${slug}`}
                      color="orange"
                      closable
                      onClose={() => removeFilter("requirementSlugs", slug)}
                    >
                      📋 {getRequirementLabel(slug)}
                    </Tag>
                  ))}
                </Space>
              </div>
            </Card>
          )}

          {/* Filter Sections using Collapse */}
          <Collapse
            defaultActiveKey={["1", "2", "3", "4"]}
            ghost
            expandIconPosition="end"
          >
            {/* Role Filter */}
            <Panel
              header={
                <Space>
                  <Text strong>Rol</Text>
                  {filters.roles.length > 0 && (
                    <Badge count={filters.roles.length} size="small" />
                  )}
                </Space>
              }
              key="1"
            >
              <Checkbox.Group
                value={filters.roles}
                onChange={handleRoleChange}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {availableRoles.map((role) => (
                    <Checkbox key={role} value={role} style={{ width: "100%" }}>
                      <Space
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                        }}
                      >
                        <span style={{ textTransform: "capitalize" }}>
                          {role}
                        </span>
                        <Tag
                          color={getRoleColor(role)}
                          style={{ fontSize: 10, margin: 0 }}
                        >
                          {role}
                        </Tag>
                      </Space>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Panel>

            {/* Resource Type Filter */}
            <Panel
              header={
                <Space>
                  <Text strong>Tipo de Recurso</Text>
                  {filters.resourceTypes.length > 0 && (
                    <Badge count={filters.resourceTypes.length} size="small" />
                  )}
                </Space>
              }
              key="2"
            >
              <Checkbox.Group
                value={filters.resourceTypes}
                onChange={handleResourceTypeChange}
                style={{ width: "100%" }}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {availableResourceTypes.map((type) => (
                    <Checkbox key={type} value={type} style={{ width: "100%" }}>
                      {type}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Panel>

            {/* Subfolder Slugs Filter */}
            {availableSubfolderSlugs.length > 0 && (
              <Panel
                header={
                  <Space>
                    <Text strong>📁 Subfolders</Text>
                    {filters.subfolderSlugs.length > 0 && (
                      <Badge
                        count={filters.subfolderSlugs.length}
                        size="small"
                      />
                    )}
                  </Space>
                }
                key="3"
              >
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  <Checkbox.Group
                    value={filters.subfolderSlugs}
                    onChange={handleSubfolderSlugChange}
                    style={{ width: "100%" }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {availableSubfolderSlugs.map((slug) => (
                        <Checkbox
                          key={slug}
                          value={slug}
                          style={{ width: "100%" }}
                        >
                          <Text ellipsis style={{ maxWidth: 300 }}>
                            {getSubfolderLabel(slug)}
                          </Text>
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </div>
              </Panel>
            )}

            {/* Requirement Slugs Filter */}
            {availableRequirementSlugs.length > 0 && (
              <Panel
                header={
                  <Space>
                    <Text strong>📋 Requisitos</Text>
                    {filters.requirementSlugs.length > 0 && (
                      <Badge
                        count={filters.requirementSlugs.length}
                        size="small"
                      />
                    )}
                  </Space>
                }
                key="4"
              >
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  <Checkbox.Group
                    value={filters.requirementSlugs}
                    onChange={handleRequirementSlugChange}
                    style={{ width: "100%" }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {availableRequirementSlugs.map((slug) => (
                        <Checkbox
                          key={slug}
                          value={slug}
                          style={{ width: "100%" }}
                        >
                          <Text ellipsis style={{ maxWidth: 300 }}>
                            {getRequirementLabel(slug)}
                          </Text>
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </div>
              </Panel>
            )}
          </Collapse>
        </div>
      </Drawer>
    </>
  );
};
